<template>
  <div
    class="asc-plot-options-wrapper"
    data-automation-id="wrapper_plot_options"
  >
    <asc-tab v-model="tabIndex" secondary>
      <asc-tab-title
        title="Data"
        :disabled="disabled || noData"
        :error="displayErrorMessage && tabsInvalid"
        :tooltip="getGlobalFilterTooltip"
      />
      <asc-tab-title
        v-if="!isAxesTabHidden"
        title="Axes"
        :disabled="disabled || noData"
        :tooltip="getGlobalFilterTooltip"
      />
      <asc-tab-title
        v-if="!isOverlaysTabHidden"
        title="Overlays"
        :disabled="disabled || noData"
        :tooltip="getGlobalFilterTooltip"
      />
    </asc-tab>
    <asc-tab-body :value="tabIndex" secondary>
      <asc-tab-item>
        <asc-plot-data-tab-wrapper
          :page-plot="pagePlot"
          :filter-lookups="filterLookups"
          :plotting-data-tabs="getDataTabs"
          :readonly="readonly"
          :saving="saving"
          :disabled="disabled"
          :editable="editable"
          :display-error-message="displayErrorMessage"
          :is-tab-menu-disabled="isTabMenuDisabled"
          @deleteTab="handleDeleteTabFromPagePlot"
          @updateTabView="handleUpdateTabViewPagePlot"
          @applyToPlot="handleApplyPagePlot"
          @filtersChange="handleApplyFiltersChange"
          @disableSubmit="handleDisableSubmit"
          @addTab="handleAddTabToPagePlot"
        ></asc-plot-data-tab-wrapper>
      </asc-tab-item>
      <asc-tab-item>
        <asc-plot-axes-tab-wrapper
          :page-plot="pagePlot"
          :chart-data="chartData"
          :active-chip="getActiveChip"
          @chipSelection="handleChipSelection"
          @autoscaleSelection="handleAutoscaleSelection"
          @filterChange="handleFilterChange"
        ></asc-plot-axes-tab-wrapper>
      </asc-tab-item>
      <asc-tab-item>
        <asc-plot-overlays-tab-wrapper
          :page-plot="pagePlot"
          :items="getOverlays"
          :on-update="handleApplyPagePlot"
          @separateChartsSelection="handleSeparateChartsSelection"
        ></asc-plot-overlays-tab-wrapper>
      </asc-tab-item>
    </asc-tab-body>
    <template v-if="!readonly && !disabled && tabIndex === 0 && editable">
      <div class="asc-plot-options-wrapper--apply-button-container">
        <asc-accent-button
          button-text="Apply Selections"
          secondary
          :on-click="handleApplySelections"
        ></asc-accent-button>
      </div>
    </template>
  </div>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscTab from '@/components/atoms/AscTab'
import AscTabBody from '@/components/atoms/AscTabBody'
import AscTabItem from '@/components/atoms/AscTabItem'
import AscTabTitle from '@/components/atoms/AscTabTitle'
import AscPlotAxesTabWrapper from '@/components/organisms/AscPlotAxesTabWrapper'
import AscPlotDataTabWrapper from '@/components/organisms/AscPlotDataTabWrapper'
import AscPlotOverlaysTabWrapper from '@/components/organisms/AscPlotOverlaysTabWrapper'
import { PLOT_TYPES, PLOT_UPDATE_MESSAGES } from '@/utilities/appConsts'
import {
  getAxesTabHiddenGivenPlotTypeId,
  getOverlaysTabHiddenGivenPlotTypeId,
} from '@/utilities/plotting'

export default {
  name: 'AscPlotOptionsWrapper',

  components: {
    AscAccentButton,
    AscTab,
    AscTabBody,
    AscTabItem,
    AscTabTitle,
    AscPlotDataTabWrapper,
    AscPlotAxesTabWrapper,
    AscPlotOverlaysTabWrapper,
  },

  props: {
    pagePlot: {
      type: Object,
      default: () => {},
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    filterLookups: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    noData: {
      type: Boolean,
      default: false,
    },
    tabsInvalid: {
      type: Boolean,
      default: false,
    },
    displayErrorMessage: {
      type: Boolean,
      default: false,
    },
    hideHiddenTabs: {
      type: Boolean,
      default: false,
    },
    onAddTabToPagePlot: {
      type: Function,
      default: () => {},
    },
    onRemoveTabFromPagePlot: {
      type: Function,
      default: () => {},
    },
    onUpdateTabViewPagePlot: {
      type: Function,
      default: () => {},
    },
    onApplyPagePlot: {
      type: Function,
      default: () => {},
    },
    onApplyInvalidPagePlot: {
      type: Function,
      default: () => {},
    },
    onDisableSubmit: {
      type: Function,
      default: () => {},
    },
    onChangeTabIndex: {
      type: Function,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isTabMenuDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tabIndex: 0,
    }
  },

  computed: {
    getGlobalFilterTooltip() {
      return this.disabled ? 'Adjust Global Filter settings to edit.' : ''
    },

    isAxesTabHidden() {
      return getAxesTabHiddenGivenPlotTypeId(this.pagePlot.plotTypeId)
    },

    isOverlaysTabHidden() {
      return getOverlaysTabHiddenGivenPlotTypeId(this.pagePlot.plotTypeId)
    },

    getOverlays() {
      return PLOT_TYPES[3].overlays.options
    },

    getActiveChip() {
      return this.pagePlot.options.axes.zoom
    },

    getDataTabs() {
      const tabs = this.pagePlot.options.data.tabs
      if (this.hideHiddenTabs) {
        const filteredTabs = tabs.filter(item => !item.hidden)
        return filteredTabs
      }

      return tabs
    },
  },

  watch: {
    pagePlot: {
      deep: true,
      handler(newPagePlot) {
        this.pagePlot = newPagePlot
        this.tabIndex = this.disabled ? 0 : newPagePlot.tabIndex || 0
      },
    },
    tabIndex(newValue) {
      if (this.onChangeTabIndex) {
        this.onChangeTabIndex(
          this.pagePlot.id,
          this.pagePlot.plotTypeId,
          newValue,
        )
      }
    },
  },

  methods: {
    handleAddTabToPagePlot(tabIndex) {
      if (this.onAddTabToPagePlot) {
        this.onAddTabToPagePlot(this.pagePlot.id, tabIndex)
      }
    },

    handleDeleteTabFromPagePlot(tabIndex) {
      if (this.onRemoveTabFromPagePlot) {
        this.onRemoveTabFromPagePlot(this.pagePlot.id, tabIndex)
      }
    },

    handleUpdateTabViewPagePlot(tabIndex, hidden) {
      if (this.onUpdateTabViewPagePlot) {
        this.onUpdateTabViewPagePlot(this.pagePlot.id, tabIndex, hidden)
      }
    },

    handleApplyPagePlot(tabId) {
      if (this.onApplyPagePlot) {
        this.onApplyPagePlot(
          this.pagePlot.id,
          this.pagePlot.plotTypeId,
          PLOT_UPDATE_MESSAGES.updatePlotData,
          tabId,
        )
      }
    },

    handleApplyFiltersChange(tabId) {
      if (this.onApplyPagePlot) {
        this.onApplyPagePlot(
          this.pagePlot.id,
          this.pagePlot.plotTypeId,
          PLOT_UPDATE_MESSAGES.updatePlotFilters,
          tabId,
        )
      }
    },

    handleApplySelections() {
      if (this.tabsInvalid && this.onApplyInvalidPagePlot) {
        this.onApplyInvalidPagePlot()
      }
      if (this.onApplyPagePlot && !this.tabsInvalid) {
        this.onApplyPagePlot(
          this.pagePlot.id,
          this.pagePlot.plotTypeId,
          PLOT_UPDATE_MESSAGES.updateAllPlotData,
        )
      }
    },

    handleDisableSubmit(tabId) {
      if (this.onDisableSubmit) {
        this.onDisableSubmit(this.pagePlot.id, tabId)
      }
    },

    handleAutoscaleSelection(chip) {
      if (this.onApplyPagePlot) {
        this.onApplyPagePlot(
          this.pagePlot.id,
          this.pagePlot.plotTypeId,
          PLOT_UPDATE_MESSAGES.updateChartAutoscale,
          chip,
        )
      }
    },

    handleSeparateChartsSelection(chip) {
      if (this.onApplyPagePlot) {
        this.onApplyPagePlot(
          this.pagePlot.id,
          this.pagePlot.plotTypeId,
          PLOT_UPDATE_MESSAGES.updateChartSeparateCharts,
          chip,
        )
      }
    },

    handleChipSelection(chip) {
      if (this.onApplyPagePlot) {
        this.onApplyPagePlot(
          this.pagePlot.id,
          this.pagePlot.plotTypeId,
          PLOT_UPDATE_MESSAGES.updateChartZoom,
          chip,
        )
      }
    },

    handleFilterChange(filter) {
      this.onApplyPagePlot(
        this.pagePlot.id,
        this.pagePlot.plotTypeId,
        PLOT_UPDATE_MESSAGES.updateChartYAxisOverrides,
        filter,
      )
    },
  },
}
</script>

<style lang="scss">
.asc-plot-options-wrapper {
  .asc-plot-options-wrapper--apply-button-container {
    background-color: $gray-100;
    padding: 16px 12px;
  }

  .asc-accent-button-container .asc-accent-button--accent.theme--light.v-btn {
    width: 100%;
  }

  .v-window__container {
    height: inherit !important;
  }
}
</style>
